import { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  api,
  ApiError,
  UPSELL_TYPE_KEYS,
  type AccountRow,
  type PortalKey,
  type UpsellGranularity,
  type UpsellTrends as Trends,
  type UpsellTypeKey,
} from '@/api/client';
import { fmtNum, fmtUsd, fmtUsdExact } from '@/lib/format';
import { Card, ErrorNote, SectionHeader, Spinner, cell, cx, headCell, mutedText, numCell, row } from '@/ui';

const TYPES: Record<UpsellTypeKey, { label: string; color: string }> = {
  earlyCheckin: { label: 'Early check-in', color: '#2563eb' },
  lateCheckout: { label: 'Late checkout', color: '#7c3aed' },
  extraNights: { label: 'Gap and extra nights', color: '#0d9488' },
  custom: { label: 'Custom', color: '#d97706' },
  other: { label: 'Other', color: '#a3a3a3' },
};

const LOCATION_COLORS = [
  '#2563eb',
  '#0d9488',
  '#d97706',
  '#7c3aed',
  '#dc2626',
  '#0891b2',
  '#db2777',
  '#65a30d',
  '#a3a3a3',
];

const GRANULARITIES: Array<{ key: UpsellGranularity; label: string }> = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
];

const axisProps = { stroke: '#a3a3a3', fontSize: 11, tickLine: false, axisLine: false } as const;

function bucketLabel(date: string, granularity: UpsellGranularity): string {
  const d = new Date(`${date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  if (granularity === 'month') return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  const day = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return granularity === 'week' ? `Week of ${day}` : day;
}

function axisLabel(date: string, granularity: UpsellGranularity): string {
  const d = new Date(`${date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  return granularity === 'month'
    ? d.toLocaleDateString('en-US', { month: 'short' })
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function pct(sold: number, offers: number): number | null {
  return offers > 0 ? (sold / offers) * 100 : null;
}

function fmtPct(value: number | null): string {
  return value == null ? '-' : `${value.toFixed(1)}%`;
}

interface TooltipEntry {
  name?: string;
  value?: number;
  color?: string;
  dataKey?: string | number;
}

function ChartTooltip({
  active,
  payload,
  label,
  granularity,
  format,
  showTotal,
}: {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string;
  granularity: UpsellGranularity;
  format: (value: number, key: string) => string;
  showTotal?: boolean;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const total = payload.reduce((sum, entry) => sum + (entry.value ?? 0), 0);
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-3 shadow-lg">
      <p className="mb-2 text-xs font-medium text-neutral-900">{bucketLabel(String(label), granularity)}</p>
      {payload.map((entry) => (
        <div key={String(entry.dataKey)} className="flex items-center justify-between gap-4 text-xs">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-neutral-500">{entry.name}</span>
          </div>
          <span className="font-medium tabular-nums text-neutral-900">
            {format(entry.value ?? 0, String(entry.dataKey))}
          </span>
        </div>
      ))}
      {showTotal && (
        <div className="mt-2 flex items-center justify-between border-t border-neutral-100 pt-2 text-xs font-medium">
          <span className="text-neutral-500">Total</span>
          <span className="tabular-nums text-neutral-900">{format(total, 'total')}</span>
        </div>
      )}
    </div>
  );
}

function Legend({ items }: { items: Array<{ key: string; label: string; color: string; value: string }> }) {
  return (
    <ul className="w-full shrink-0 space-y-1 lg:w-56 lg:border-l lg:border-neutral-200 lg:pl-4">
      {items.map((item) => (
        <li key={item.key} className="flex items-center justify-between gap-3 px-2 py-1 text-xs text-neutral-700">
          <div className="flex min-w-0 items-center gap-2">
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
            <span className="truncate">{item.label}</span>
          </div>
          <span className="font-medium tabular-nums">{item.value}</span>
        </li>
      ))}
    </ul>
  );
}

function ChartFrame({ children, legend }: { children: React.ReactElement; legend?: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-4 p-4 lg:flex-row">
      <div className="h-72 min-w-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </div>
      {legend}
    </div>
  );
}

export function UpsellTrends({
  portal,
  accounts,
  start,
  end,
  userIds,
  reloadKey,
}: {
  portal: PortalKey;
  accounts: AccountRow[];
  start: string;
  end: string;
  userIds: number[];
  reloadKey: number;
}) {
  const [granularity, setGranularity] = useState<UpsellGranularity>('week');
  const [data, setData] = useState<Trends | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const idsKey = userIds.join(',');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .upsells(portal, start, end, granularity, userIds)
      .then((next) => {
        if (!cancelled) setData(next);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          window.location.reload();
          return;
        }
        setError('Could not load upsell trends. Try again in a moment.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // userIds is tracked through idsKey, so a new array with the same ids does not refetch.
  }, [portal, start, end, granularity, idsKey, reloadKey]);

  const g = data?.granularity ?? granularity;

  const byType = useMemo(() => {
    const totals = Object.fromEntries(
      UPSELL_TYPE_KEYS.map((key) => [key, { offers: 0, sold: 0, revenueUsdCents: 0 }])
    ) as Trends['series'][number]['byType'];
    data?.series.forEach((point) =>
      UPSELL_TYPE_KEYS.forEach((key) => {
        totals[key].offers += point.byType[key].offers;
        totals[key].sold += point.byType[key].sold;
        totals[key].revenueUsdCents += point.byType[key].revenueUsdCents;
      })
    );
    return totals;
  }, [data]);

  const revenueRows = useMemo(
    () =>
      (data?.series ?? []).map((point) => ({
        date: point.date,
        ...Object.fromEntries(UPSELL_TYPE_KEYS.map((key) => [key, point.byType[key].revenueUsdCents])),
      })),
    [data]
  );

  const activityRows = useMemo(
    () =>
      (data?.series ?? []).map((point) => ({
        date: point.date,
        offers: point.offers,
        sold: point.sold,
        conversion: pct(point.sold, point.offers),
      })),
    [data]
  );

  const scopedAccounts = useMemo(
    () => (userIds.length ? accounts.filter((a) => userIds.includes(a.userId)) : accounts),
    [accounts, userIds]
  );

  const locationRows = useMemo(
    () =>
      (data?.locations ?? []).map((point) => ({
        date: point.date,
        ...Object.fromEntries(scopedAccounts.map((a) => [`u${a.userId}`, point.byUser[String(a.userId)] ?? 0])),
      })),
    [data, scopedAccounts]
  );

  const totalRevenue = UPSELL_TYPE_KEYS.reduce((sum, key) => sum + byType[key].revenueUsdCents, 0);
  const totalOffers = UPSELL_TYPE_KEYS.reduce((sum, key) => sum + byType[key].offers, 0);
  const totalSold = UPSELL_TYPE_KEYS.reduce((sum, key) => sum + byType[key].sold, 0);
  const maxTypeRevenue = Math.max(...UPSELL_TYPE_KEYS.map((key) => byType[key].revenueUsdCents), 0);
  const empty = data != null && totalOffers === 0 && totalSold === 0;
  const tick = (date: string) => axisLabel(date, g);
  const nameOf = (a: AccountRow) => a.fullName?.trim() || a.email;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-neutral-900">Upsells</h2>
          <p className={mutedText}>Offers, sales and revenue by type and over time</p>
        </div>
        <div className="flex items-center gap-2">
          {loading && (
            <span className="h-3 w-3 animate-spin rounded-full border border-neutral-200 border-t-neutral-900" />
          )}
          <div className="flex items-center gap-1">
            {GRANULARITIES.map((option) => (
              <button
                key={option.key}
                disabled={loading}
                onClick={() => setGranularity(option.key)}
                className={cx(
                  'rounded border px-2 py-1 text-xs font-medium transition-colors disabled:opacity-40',
                  granularity === option.key
                    ? 'border-neutral-200 bg-white text-neutral-950 shadow-[0_1px_2px_rgb(24_35_47/0.08)]'
                    : 'border-transparent text-neutral-500 hover:text-neutral-900'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}
      {empty && (
        <div className="rounded-lg border border-neutral-200 bg-neutral-50/60 px-4 py-2.5 text-xs text-neutral-600">
          No upsell offers or sales in this range yet. Charts fill in once locations go live on inbox.
        </div>
      )}

      {!data ? (
        <Card className="p-4">
          <Spinner label="Loading upsells" />
        </Card>
      ) : (
        <>
          <Card>
            <SectionHeader title="Upsells by type" subtitle="Revenue, sales and conversion in the selected range" />
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className={headCell}>Type</th>
                    <th className={cx(headCell, 'w-[40%]')}>Share of revenue</th>
                    <th className={cx(headCell, 'text-right')}>Revenue</th>
                    <th className={cx(headCell, 'text-right')}>Sold</th>
                    <th className={cx(headCell, 'text-right')}>Offers sent</th>
                    <th className={cx(headCell, 'text-right')}>Conversion</th>
                  </tr>
                </thead>
                <tbody>
                  {UPSELL_TYPE_KEYS.map((key) => {
                    const stats = byType[key];
                    const width = maxTypeRevenue > 0 ? (stats.revenueUsdCents / maxTypeRevenue) * 100 : 0;
                    return (
                      <tr key={key} className={row}>
                        <td className={cell}>
                          <div className="flex items-center gap-2">
                            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: TYPES[key].color }} />
                            <span className="font-medium text-neutral-900">{TYPES[key].label}</span>
                          </div>
                        </td>
                        <td className={cell}>
                          <div className="flex items-center gap-2">
                            <div className="h-2 flex-1 overflow-hidden rounded-full bg-neutral-100">
                              <div
                                className="h-full rounded-full"
                                style={{ width: `${width}%`, backgroundColor: TYPES[key].color }}
                              />
                            </div>
                            <span className="w-10 text-right tabular-nums text-neutral-500">
                              {totalRevenue > 0 ? `${Math.round((stats.revenueUsdCents / totalRevenue) * 100)}%` : '-'}
                            </span>
                          </div>
                        </td>
                        <td className={numCell}>{fmtUsdExact(stats.revenueUsdCents)}</td>
                        <td className={numCell}>{fmtNum(stats.sold)}</td>
                        <td className={numCell}>{fmtNum(stats.offers)}</td>
                        <td className={numCell}>{fmtPct(pct(stats.sold, stats.offers))}</td>
                      </tr>
                    );
                  })}
                  <tr className="bg-neutral-50/60">
                    <td className={cx(cell, 'font-semibold text-neutral-900')}>Total</td>
                    <td className={cell} />
                    <td className={cx(numCell, 'font-semibold text-neutral-900')}>{fmtUsdExact(totalRevenue)}</td>
                    <td className={cx(numCell, 'font-semibold text-neutral-900')}>{fmtNum(totalSold)}</td>
                    <td className={cx(numCell, 'font-semibold text-neutral-900')}>{fmtNum(totalOffers)}</td>
                    <td className={cx(numCell, 'font-semibold text-neutral-900')}>
                      {fmtPct(pct(totalSold, totalOffers))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>

          <Card>
            <SectionHeader title="Upsell revenue over time" subtitle="Stacked by upsell type" />
            <ChartFrame
              legend={
                <Legend
                  items={UPSELL_TYPE_KEYS.map((key) => ({
                    key,
                    label: TYPES[key].label,
                    color: TYPES[key].color,
                    value: fmtUsd(byType[key].revenueUsdCents),
                  }))}
                />
              }
            >
              <BarChart data={revenueRows} margin={{ top: 20, right: 20, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ebebeb" vertical={false} />
                <XAxis dataKey="date" tickFormatter={tick} {...axisProps} />
                <YAxis {...axisProps} tickFormatter={(v: number) => fmtUsd(v, { compact: true })} />
                <Tooltip
                  cursor={{ fill: '#f5f5f5' }}
                  content={<ChartTooltip granularity={g} format={(v) => fmtUsdExact(v)} showTotal />}
                />
                {UPSELL_TYPE_KEYS.map((key, i) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    name={TYPES[key].label}
                    stackId="revenue"
                    fill={TYPES[key].color}
                    radius={i === UPSELL_TYPE_KEYS.length - 1 ? [3, 3, 0, 0] : undefined}
                    maxBarSize={36}
                  />
                ))}
              </BarChart>
            </ChartFrame>
          </Card>

          <div className="grid gap-4 xl:grid-cols-2">
            <Card>
              <SectionHeader title="Offers sent vs upsells sold" subtitle="Conversion rate on the right axis" />
              <ChartFrame>
                <ComposedChart data={activityRows} margin={{ top: 20, right: 10, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ebebeb" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={tick} {...axisProps} />
                  <YAxis yAxisId="count" {...axisProps} tickFormatter={fmtNum} allowDecimals={false} />
                  <YAxis
                    yAxisId="pct"
                    orientation="right"
                    {...axisProps}
                    domain={[0, 100]}
                    tickFormatter={(v: number) => `${v}%`}
                  />
                  <Tooltip
                    cursor={{ fill: '#f5f5f5' }}
                    content={
                      <ChartTooltip
                        granularity={g}
                        format={(v, key) => (key === 'conversion' ? fmtPct(v) : fmtNum(v))}
                      />
                    }
                  />
                  <Bar
                    yAxisId="count"
                    dataKey="offers"
                    name="Offers sent"
                    fill="#dbeafe"
                    radius={[3, 3, 0, 0]}
                    maxBarSize={28}
                  />
                  <Bar
                    yAxisId="count"
                    dataKey="sold"
                    name="Upsells sold"
                    fill="#2563eb"
                    radius={[3, 3, 0, 0]}
                    maxBarSize={28}
                  />
                  <Line
                    yAxisId="pct"
                    type="monotone"
                    dataKey="conversion"
                    name="Conversion"
                    stroke="#0d9488"
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                    activeDot={{ r: 4, fill: '#0d9488', stroke: '#ffffff', strokeWidth: 2 }}
                  />
                </ComposedChart>
              </ChartFrame>
            </Card>

            <Card>
              <SectionHeader title="Revenue by location" subtitle="Upsell revenue per location over time" />
              <ChartFrame
                legend={
                  <Legend
                    items={scopedAccounts.map((a, i) => ({
                      key: String(a.userId),
                      label: nameOf(a),
                      color: LOCATION_COLORS[i % LOCATION_COLORS.length],
                      value: fmtUsd(
                        (data.locations ?? []).reduce((sum, p) => sum + (p.byUser[String(a.userId)] ?? 0), 0)
                      ),
                    }))}
                  />
                }
              >
                <LineChart data={locationRows} margin={{ top: 20, right: 10, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ebebeb" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={tick} {...axisProps} />
                  <YAxis {...axisProps} tickFormatter={(v: number) => fmtUsd(v, { compact: true })} />
                  <Tooltip content={<ChartTooltip granularity={g} format={(v) => fmtUsdExact(v)} showTotal />} />
                  {scopedAccounts.map((a, i) => (
                    <Line
                      key={a.userId}
                      type="monotone"
                      dataKey={`u${a.userId}`}
                      name={nameOf(a)}
                      stroke={LOCATION_COLORS[i % LOCATION_COLORS.length]}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4, stroke: '#ffffff', strokeWidth: 2 }}
                    />
                  ))}
                </LineChart>
              </ChartFrame>
            </Card>
          </div>

          <Card>
            <SectionHeader title="Top custom upsells" subtitle="Custom upsell items ranked by revenue" />
            {data.customItems.length === 0 ? (
              <div className={cx('px-6 pb-5', mutedText)}>No custom upsells sold in this range.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className={headCell}>Item</th>
                      <th className={cx(headCell, 'text-right')}>Revenue</th>
                      <th className={cx(headCell, 'text-right')}>Sold</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.customItems.map((item) => (
                      <tr key={item.name} className={row}>
                        <td className={cx(cell, 'font-medium text-neutral-900')}>{item.name}</td>
                        <td className={numCell}>{fmtUsdExact(item.revenueUsdCents)}</td>
                        <td className={numCell}>{fmtNum(item.sold)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
